import { useEffect, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AVATARS, GAME_CONFIG, normalizeRoomCode, sanitizeNickname } from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { Button, Card, CodeInput, Heading, Input, Label, Row, Stack, Subtle, Title } from '../components/ui';
import { wobble } from '../styles/GlobalStyle';

const Hero = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const Mascot = styled.div`
  font-size: clamp(3rem, 12vw, 4.6rem);
  line-height: 1;
  animation: ${wobble} 3.4s ease-in-out infinite;
  transform-origin: 50% 90%;
`;

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
  gap: 8px;
`;

const AvatarButton = styled.button<{ $active: boolean }>`
  aspect-ratio: 1;
  font-size: 26px;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme, $active }) => ($active ? theme.colors.bubblegum : theme.colors.cream)};
  border: 2.5px solid
    ${({ theme, $active }) => ($active ? theme.colors.bubblegumDeep : theme.colors.surfaceSunken)};
  transform: ${({ $active }) => ($active ? 'scale(1.08)' : 'none')};
  transition: all 200ms ${({ theme }) => theme.transitions.bounce};

  &:hover {
    transform: scale(1.12) rotate(-4deg);
  }
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  color: ${({ theme }) => theme.colors.inkSoft};
  line-height: 1.5;

  li::marker {
    color: ${({ theme }) => theme.colors.bubblegumDeep};
    font-weight: 800;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.inkFaint};
  font-weight: 700;
  font-size: 0.85rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.surfaceSunken};
  }
`;

/** Shown instead of the code field when arriving from a share link. */
const InviteCard = styled(Card)`
  text-align: center;
  background: linear-gradient(120deg, #fff0f8, #f2f0ff 50%, #eaf6ff);
`;

const InviteCode = styled.div`
  font-size: clamp(2.2rem, 11vw, 3.4rem);
  font-weight: 800;
  letter-spacing: 0.2em;
  text-indent: 0.2em;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.ink};
`;

const PREFS_KEY = 'mr.prefs';

interface Prefs {
  nickname: string;
  avatar: string;
}

function loadPrefs(): Prefs {
  const fallback: Prefs = {
    nickname: '',
    avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)] as string,
  };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      nickname: typeof parsed.nickname === 'string' ? parsed.nickname : fallback.nickname,
      avatar:
        typeof parsed.avatar === 'string' && (AVATARS as readonly string[]).includes(parsed.avatar)
          ? parsed.avatar
          : fallback.avatar,
    };
  } catch {
    return fallback;
  }
}

export function HomeScreen() {
  const { t } = useTranslation();
  const { actions, status, pendingCode } = useGame();

  const [prefs] = useState(loadPrefs);
  const [nickname, setNickname] = useState(prefs.nickname);
  const [avatar, setAvatar] = useState(prefs.avatar);
  // Comes from the provider so that dismissing the invite from the top bar is
  // reflected here immediately.
  const invitedCode = pendingCode;
  const [code, setCode] = useState(invitedCode ?? '');

  // Remember the player's identity so they do not retype it every session.
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ nickname, avatar } satisfies Prefs));
    } catch {
      /* ignore */
    }
  }, [nickname, avatar]);

  const trimmedName = sanitizeNickname(nickname);
  const canCreate = trimmedName !== null && status !== 'closed';
  const canJoin = canCreate && (invitedCode ?? normalizeRoomCode(code)).length === GAME_CONFIG.roomCodeLength;

  const handleCreate = () => {
    if (canCreate && trimmedName) actions.createLobby(trimmedName, avatar);
  };

  const joinCode = invitedCode ?? normalizeRoomCode(code);

  const handleJoin = (event: FormEvent) => {
    event.preventDefault();
    if (canJoin && trimmedName) actions.joinLobby(joinCode, trimmedName, avatar);
  };

  return (
    <>
      <Hero>
        <Mascot aria-hidden="true">🫧</Mascot>
        <Title>{t('app.title')}</Title>
        <Subtle $center>{t('app.tagline')}</Subtle>
      </Hero>

      {invitedCode && (
        <InviteCard>
          <Stack $gap="6px" $align="center">
            <Subtle>{t('home.invitedTo')}</Subtle>
            <InviteCode>{invitedCode}</InviteCode>
            <Subtle $center>{t('home.invitedHint')}</Subtle>
          </Stack>
        </InviteCard>
      )}

      <Card>
        <Stack $gap="20px">
          <div>
            <Label htmlFor="nickname">{t('home.nicknameLabel')}</Label>
            <Input
              id="nickname"
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
              placeholder={t('home.nicknamePlaceholder')}
              maxLength={GAME_CONFIG.nicknameMaxLength}
              autoComplete="nickname"
            />
          </div>

          <div>
            <Label as="span">{t('home.avatarLabel')}</Label>
            <AvatarGrid role="radiogroup" aria-label={t('home.avatarLabel')}>
              {AVATARS.map((option) => (
                <AvatarButton
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={avatar === option}
                  aria-label={option}
                  $active={avatar === option}
                  onClick={() => setAvatar(option)}
                >
                  {option}
                </AvatarButton>
              ))}
            </AvatarGrid>
          </div>

          {/* Arriving from a share link, the code is already settled: all that is
              left is a name, so the code field would just be a hurdle. */}
          {invitedCode ? (
            <form onSubmit={handleJoin}>
              <Button type="submit" $tone="primary" $size="lg" $full disabled={!canJoin}>
                🚪 {t('home.joinRoomButton', { code: invitedCode })}
              </Button>
            </form>
          ) : (
            <>
              {/* Joining comes first: most people arrive with a code from a friend,
                  and only one person per game ever needs the create button. */}
              <Divider>{t('home.joinTitle')}</Divider>

              <form onSubmit={handleJoin}>
                <Row $gap="10px" $wrap>
                  <div style={{ flex: '1 1 180px' }}>
                    <CodeInput
                      value={code}
                      onChange={(event) => setCode(normalizeRoomCode(event.target.value))}
                      placeholder={t('home.codePlaceholder')}
                      maxLength={GAME_CONFIG.roomCodeLength}
                      aria-label={t('home.codePlaceholder')}
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  <Button type="submit" $tone="primary" disabled={!canJoin}>
                    {t('home.joinButton')} →
                  </Button>
                </Row>
              </form>

              <Divider>{t('home.createTitle')}</Divider>

              <Button type="button" $tone="secondary" $size="lg" $full disabled={!canCreate} onClick={handleCreate}>
                ✨ {t('home.createButton')}
              </Button>
            </>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack $gap="12px">
          <Heading>{t('home.howToTitle')}</Heading>
          <Steps>
            <li>{t('home.howTo1')}</li>
            <li>{t('home.howTo2')}</li>
            <li>{t('home.howTo3')}</li>
          </Steps>
        </Stack>
      </Card>
    </>
  );
}
